import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, Navigate } from "react-router-dom";
import { getProject, getNext, slugRedirects, categoryLabel, sectionItems, asset } from "../data/portfolio";
import { CoverVideo, NativeImage, VideoItem } from "../components/media";
import { Reveal } from "../components/motion";

function ImageRow({ images, section, onOpen }) {
  if (!images.length) return null;
  return (
    <div className="jrow">
      {images.map((it, idx) => {
        const ratio = it.w && it.h ? it.w / it.h : 1.4;
        return (
          <figure key={idx} className="jfig" style={{ flexGrow: ratio, flexBasis: `${Math.round(ratio * 180)}px` }}>
            <span className="block w-full cursor-zoom-in" data-testid={`sec-item-${section.id}-${idx}`}>
              <NativeImage src={it.src} w={it.w} h={it.h} alt={section.title} className="rounded-[4px]" />
            </span>
          </figure>
        );
      })}
    </div>
  );
}

// Horizontal scroller with nudging lime arrows (carousels / film-reel rails).
function HScroller({ items, section, onOpen, controls, reels = false }) {
  const trackRef = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const update = () => {
    const t = trackRef.current;
    if (!t) return;
    setCanL(t.scrollLeft > 4);
    setCanR(t.scrollLeft + t.clientWidth < t.scrollWidth - 4);
  };
  useEffect(() => {
    const t = trackRef.current;
    if (!t) return;
    update();
    const id = setTimeout(update, 400);
    const id2 = setTimeout(update, 1200);
    t.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // re-check once media (images/videos) report their real size
    const media = t.querySelectorAll("img,video");
    media.forEach((m) => {
      m.addEventListener("load", update);
      m.addEventListener("loadedmetadata", update);
    });
    const ro = new ResizeObserver(update);
    ro.observe(t);
    return () => {
      clearTimeout(id); clearTimeout(id2);
      t.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      media.forEach((m) => { m.removeEventListener("load", update); m.removeEventListener("loadedmetadata", update); });
      ro.disconnect();
    };
  }, [items]);
  const nudge = (dir) => {
    const t = trackRef.current;
    if (!t) return;
    t.scrollBy({ left: dir * t.clientWidth * 0.8, behavior: "smooth" });
  };
  return (
    <div className="hs">
      <button className="arr l" hidden={!canL} onClick={() => nudge(-1)} aria-label="Previous" data-testid={`carousel-prev-${section.id}`}>←</button>
      <div className="track" ref={trackRef}>
        <div className={reels ? "carousel reels" : "carousel"}>
          {items.map((it, idx) => (
            <figure key={idx} className="rail-fig" style={{ cursor: it.type === "video" ? "default" : "zoom-in" }}>
              {it.type === "video"
                ? <VideoItem src={it.src} poster={it.poster} w={it.w} h={it.h} controls={controls} rail />
                : <span className="block h-full cursor-zoom-in" data-testid={`sec-item-${section.id}-${idx}`}>
                    <NativeImage src={it.src} w={it.w} h={it.h} alt={section.title} style={{ height: "100%", width: "auto" }} />
                  </span>}
            </figure>
          ))}
        </div>
      </div>
      <button className="arr r" hidden={!canR} onClick={() => nudge(1)} aria-label="Next" data-testid={`carousel-next-${section.id}`}>→</button>
    </div>
  );
}

function SectionBlock({ section, onOpen }) {
  const sets = sectionItems(section);
  return (
    <section id={section.id} className="scroll-mt-24 sec-block rule-t">
      <h2 className="h2" style={{ fontSize: "clamp(1.4rem,2.6vw,2.2rem)" }}>{section.title}</h2>
      {section.what_i_did && (
        <p className="sec-did"><span className="did-tag">What I did</span>{section.what_i_did}</p>
      )}
      {sets.map((set, si) => {
        const inContext = /in context/i.test(set.label || "");
        const items = inContext ? (set.items || []).slice(0, 3) : (set.items || []);
        const videos = items.filter((it) => it.type === "video");
        const images = items.filter((it) => it.type !== "video");
        const inMotion = set.label === "In motion";
        const allVideo = items.length > 0 && videos.length === items.length;
        const portrait = videos.length > 0 && videos.every((v) => (v.h || 0) > (v.w || 0));
        const reelRow = !inMotion && allVideo && portrait;      // films/reels → one horizontal row
        const isCarousel = /carousel/i.test(set.label || "");
        return (
          <div key={si} className="set-block">
            {set.label && <div className="mono set-lab" style={{ color: "var(--grey)" }}>{set.label}</div>}
            {reelRow || isCarousel ? (
              <HScroller items={items} section={section} onOpen={onOpen} controls={!inMotion} reels={reelRow} />
            ) : (
              <>
                {videos.map((v, i) => <VideoItem key={`v${i}`} src={v.src} poster={v.poster} w={v.w} h={v.h} controls={!inMotion} />)}
                <ImageRow images={images} section={section} onOpen={onOpen} />
              </>
            )}
          </div>
        );
      })}
    </section>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();

  if (slugRedirects[slug]) return <Navigate to={`/work/${slugRedirects[slug]}`} replace />;
  const project = getProject(slug);
  if (!project) return <Navigate to="/work" replace />;

  const next = getNext(slug);
  const sections = project.sections || [];

  const meta = [
    ["Client", project.client], ["Role", project.role], ["Year", project.year],
    ["Industry", project.industry], ["Category", categoryLabel(project.category)],
    ["Deliverables", (project.deliverables || []).join(" · ")],
  ];

  return (
    <div data-testid="case-study-page">
      <Helmet>
        <title>{`${project.title} — ${project.client} | vdesigns.eps`}</title>
        <meta property="og:image" content={asset(project.cover?.poster)} />
      </Helmet>

      {/* HERO (max 68vh) */}
      <section className="relative" style={{ height: "68vh", minHeight: 420, overflow: "hidden" }}>
        <CoverVideo key={project.slug} cover={project.cover} hd ratio="16 / 9" playing style={{ height: "100%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(13,13,18,.35) 0%, rgba(13,13,18,.08) 40%, rgba(13,13,18,.8) 100%)" }} />
        <div className="absolute inset-0 page-x flex flex-col justify-between py-20" style={{ color: "#fff" }}>
          <div className="wrap w-full flex items-center justify-between">
            <Link to={project.section === "playground" ? "/playground" : "/work"} className="ftag ftag--lime">← {project.section === "playground" ? "Playground" : "All work"}</Link>
            <span className="mono">({String(project.order).padStart(2, "0")})</span>
          </div>
          <div className="wrap w-full">
            <div className="flex gap-2 mb-2">
              {(project.tags || []).map((t) => <span key={t} className="ftag ftag--lime">{t.toUpperCase()}</span>)}
            </div>
            <p className="serif mb-1" style={{ fontSize: "clamp(1rem,1.6vw,1.5rem)", color: "var(--lime)" }}>{project.industry} —</p>
            <h1 className="display" style={{ fontSize: "clamp(2.6rem,6vw,5.5rem)", color: "#fff" }}>{project.title}</h1>
          </div>
        </div>
      </section>

      {/* META */}
      <section className="page-x py-8 rule-b">
        <div className="wrap grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {meta.map(([k, v]) => (
            <div key={k}><div className="mono mb-1" style={{ color: "var(--grey)" }}>{k}</div><div style={{ fontSize: 13, lineHeight: 1.3 }}>{v || "—"}</div></div>
          ))}
        </div>
      </section>

      {/* SUMMARY — full-width sub-copy */}
      <section className="page-x pt-12 pb-2">
        <div className="wrap">
          <Reveal>
            <p className="cs-sub" data-testid="case-summary">{project.summary}</p>
            <p className="mono mt-5" style={{ color: "var(--grey)" }}>In this project: {sections.map((s) => s.title).join(" · ")}</p>
          </Reveal>
        </div>
      </section>

      {/* BODY with sticky index */}
      <section className="page-x pb-12">
        <div className="wrap grid lg:grid-cols-[220px_1fr] gap-10">
          {/* sticky index */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="mono mb-3" style={{ color: "var(--blue)" }}>(IN THIS PROJECT)</p>
              <ol className="flex flex-col gap-2">
                {sections.map((s, i) => (
                  <li key={s.id}><a href={`#${s.id}`} className="ul mono" style={{ color: "var(--ink)" }}>{String(i + 1).padStart(2, "0")} {s.title} <span style={{ color: "var(--grey)" }}>({s.count || (s.sets || []).reduce((n, st) => n + (st.items || []).length, 0)})</span></a></li>
                ))}
              </ol>
            </div>
          </aside>

          <div className="min-w-0">
            {sections.map((s) => <SectionBlock key={s.id} section={s} />)}
          </div>
        </div>
      </section>

      {/* NEXT */}
      <Link to={`/work/${next.slug}`} className="relative block" style={{ height: "48vh", minHeight: 320, overflow: "hidden" }} data-testid="next-project">
        <CoverVideo key={`next-${next.slug}`} cover={next.cover} ratio="21 / 9" playing style={{ height: "100%" }} />
        <div className="absolute inset-0" style={{ background: "rgba(30,34,255,.55)", mixBlendMode: "multiply" }} />
        <div className="absolute inset-0 page-x flex flex-col items-center justify-center text-center" style={{ color: "#fff" }}>
          <span className="mono mb-3" style={{ color: "var(--lime)" }}>Next file →</span>
          <h2 className="display" style={{ fontSize: "clamp(2.4rem,7vw,7rem)", color: "#fff" }}>{next.title} <span className="go" aria-hidden>→</span></h2>
        </div>
      </Link>
    </div>
  );
}
